// ─────────────────────────────────────────────────────────────────────────
// AV Simulation Tool — Surface Image Size measurement
//
// Pure read-only measurement utility. Casts 5 rays from a projector
// (centre + 4 frustum corners) into the scene, intersects whatever
// surfaces are in the beam, and reports the actual projected image
// dimensions on those surfaces.
//
// IMPORTANT: This file does not modify the projection system, the
// projection shader, materials, lighting, beam logic, lens calc, or the
// scene graph. It only consumes existing projector data and calls
// scene.pickWithRay() — a non-mutating Babylon API.
// ─────────────────────────────────────────────────────────────────────────

import * as BABYLON from '@babylonjs/core';
import { getProjectorDirectionAndUp, getProjectionOrigin } from '../components/Editor/sceneUtils';

const MAX_RAY_DISTANCE = 1000; // metres — effectively infinite for any installation

// Default predicate: any visible, pickable, non-projector geometry counts as
// a possible projection surface. We exclude:
//   1. Anything whose mesh name ends with the projector's id (its body, lens,
//      frame, clamp, beam edges, beam center line — all use `_<projectorId>`).
//   2. Generic helper meshes (axes, gizmos, origin markers, grid, stacking
//      ghosts, frustum debug lines) by name pattern.
//   3. Anything explicitly tagged `metadata.isHelper === true`.
const HELPER_NAME_PATTERNS = /^(gizmo|axes|originMarker|projHelper|projVis|projGhost|projLabel|projDistLabel|projCenterLine|frustum|debugLine|grid_|stack_|imageSizeOverlay_)/i;

const isHelperMesh = (mesh) => {
  if (!mesh || !mesh.name) return true;
  if (HELPER_NAME_PATTERNS.test(mesh.name)) return true;
  if (mesh.metadata?.isHelper === true) return true;
  return false;
};

/**
 * Parse an aspect-ratio string ("16:10", "16:9", "1.85:1") into a numeric ratio.
 */
const parseAspect = (aspect) => {
  if (typeof aspect === 'number' && isFinite(aspect) && aspect > 0) return aspect;
  if (typeof aspect !== 'string') return 16 / 10;
  const m = aspect.match(/^\s*([\d.]+)\s*[:xX/]\s*([\d.]+)\s*$/);
  if (m) {
    const w = parseFloat(m[1]);
    const h = parseFloat(m[2]);
    if (isFinite(w) && isFinite(h) && h > 0) return w / h;
  }
  const n = parseFloat(aspect);
  return isFinite(n) && n > 0 ? n : 16 / 10;
};

/**
 * Cast a ray and return { mesh, point } if it hits a non-helper, non-self surface.
 * @param {Set<string>} ownNameSuffix  e.g. '_pjr-abc' — meshes whose name ends with
 *                                     this string are owned by the firing projector.
 */
const castSurfaceRay = (scene, origin, direction, ownNameSuffix) => {
  const ray = new BABYLON.Ray(origin, direction, MAX_RAY_DISTANCE);
  const predicate = (mesh) => {
    if (!mesh || mesh.isDisposed?.()) return false;
    if (mesh.isPickable === false) return false;
    if (!mesh.isEnabled?.() || mesh.isVisible === false) return false;
    // Skip meshes owned by THIS projector — body, lens, frame, beam, clamp.
    if (ownNameSuffix && mesh.name && mesh.name.endsWith(ownNameSuffix)) return false;
    if (isHelperMesh(mesh)) return false;
    if (!mesh.getTotalVertices || mesh.getTotalVertices() === 0) return false;
    return true;
  };
  const info = scene.pickWithRay(ray, predicate, false);
  if (!info || !info.hit || !info.pickedPoint) return null;
  return { mesh: info.pickedMesh, point: info.pickedPoint.clone() };
};

/**
 * Compute the actual projected image footprint on whatever surface(s) the
 * projector is aimed at.
 *
 * @param {Object} projector  Projector state object (position, rotation, image_width, etc.)
 * @param {BABYLON.Scene} scene
 * @param {Object} options
 * @param {Object} options.projectorMeshes  projectorMeshesRef.current — used to
 *                                          exclude the projector's own meshes from hits.
 * @returns {{
 *   topEdge: number,        // metres — top edge length on the surface
 *   bottomEdge: number,
 *   leftEdge: number,
 *   rightEdge: number,
 *   diagonal: number,       // tl→br
 *   throwDistanceCenter: number, // metres — origin → centre hit
 *   primarySurfaceName: string|null,
 *   surfaceMatchPct: number,// 0..1 — fraction of corner rays that hit the same primary surface
 *   cornerHits: { tl, tr, bl, br, c }, // BABYLON.Vector3 | null per corner
 *   status: 'ok' | 'mixed' | 'no-surface' | 'reverse'
 * } | null}
 *
 * Returns null if the projector is invalid (no position/orientation data).
 */
export function computeSurfaceImageSize(projector, scene, options = {}) {
  if (!projector || !scene) return null;

  // Reverse projectors paint a UV-mapped texture onto an object — beam-corner
  // raycasting doesn't represent their output. Skip cleanly.
  if (projector.is_reverse_projector) {
    return {
      status: 'reverse',
      topEdge: 0, bottomEdge: 0, leftEdge: 0, rightEdge: 0, diagonal: 0,
      throwDistanceCenter: 0, primarySurfaceName: null, surfaceMatchPct: 0,
      cornerHits: { tl: null, tr: null, bl: null, br: null, c: null },
    };
  }

  const projectorMeshes = options.projectorMeshes || {};
  // Suffix that tags every mesh owned by this projector (body, lens, frame,
  // clamp, beam edges, beam center, lens90 pipes/joint/glass, etc.).
  // All such meshes use the pattern `<role>_<projectorId>`.
  const ownNameSuffix = '_' + projector.id;

  // ----- Frustum geometry (in world space) -----
  // For UST, the cyan overlay must trace the SAME cone that the projection
  // matrix uses (ProjectionSystem.js) — not the beam wireframe — so the
  // measured rectangle on the wall lines up exactly with the rendered
  // green-grid image. UST matrix apex = mirror world position + body-local
  // (+30mm Y, +100mm Z) nudge. For all other lenses, we keep using the
  // beam-wireframe apex (single source via getProjectionOrigin).
  let origin = getProjectionOrigin(projector, projectorMeshes);
  if (projector.lensType === 'ust' && scene && scene.getMeshByName) {
    const mirrorMesh = scene.getMeshByName('ustGlassv4_' + projector.id);
    if (mirrorMesh) {
      mirrorMesh.computeWorldMatrix(true);
      const mirrorPos = mirrorMesh.getAbsolutePosition().clone();
      const rotX = BABYLON.Tools.ToRadians(projector.rotation?.x || 0);
      const rotY = BABYLON.Tools.ToRadians(projector.rotation?.y || 0);
      const rotZ = BABYLON.Tools.ToRadians(projector.rotation?.z || 0);
      const rotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
      // MUST stay in sync with ProjectionSystem.js UST apex nudge (0, 0.03, 0.10)
      const ustNudgeLocal = new BABYLON.Vector3(0, 0.03, 0.10);
      const ustNudgeWorld = BABYLON.Vector3.TransformNormal(ustNudgeLocal, rotMatrix);
      origin = mirrorPos.add(ustNudgeWorld);
    }
  }
  const { direction, up } = getProjectorDirectionAndUp(projector);

  // Image plane defined by throw_distance, the projector lens throw_ratio
  // (image-width-in-metres = throw_distance / throw_ratio — same formula
  // the existing Beam / Throw UI uses), and the aspect ratio.
  // Note: `projector.image_width` stores native pixel resolution (e.g. 1920),
  // NOT metres — we must derive metres from throw_ratio here.
  // Apply zoom (effective throw ratio) so the overlay matches the actual
  // projection rendered by ProjectionSystem.js, which also uses
  // effectiveThrowRatio = throw_ratio * zoom.
  const throwDist = Math.max(0.05, parseFloat(projector.throw_distance) || 5);
  const throwRatio = parseFloat(projector.throw_ratio) || 1.5;
  const zoom = (parseFloat(projector.zoom) || 100) / 100;
  const effectiveThrowRatio = throwRatio * zoom;
  const imgW = Math.max(0.01, throwDist / effectiveThrowRatio);
  const aspect = parseAspect(projector.aspect_ratio);
  const imgH = imgW / aspect;
  const halfW = imgW / 2;
  const halfH = imgH / 2;

  // Right axis perpendicular to direction & up (Babylon left-handed → cross order).
  const right = BABYLON.Vector3.Cross(up, direction).normalize();
  const trueUp = BABYLON.Vector3.Cross(direction, right).normalize();

  // Lens shift — must mirror the BEAM wireframe (BabylonScene.js) and the
  // PROJECTION matrix (ProjectionSystem.js): UST auto-adds +70% vertical
  // bias, Periscope auto-adds +45% vertical bias so the projection lands
  // HIGH on the wall (real projector behaviour). User's lens_shift_v stays
  // additive on top. The shift offsets the entire frustum by
  // (lensShiftH * imgW, lensShiftV * imgH) at the throw plane — independent
  // of throw distance because (shift fraction) × (full width at that
  // distance) scales linearly with distance, exactly like the cone itself.
  let autoVBias = 0;
  if (projector.lensType === 'ust') autoVBias = 70;
  else if (projector.lensType === 'topBottom') {
    // Periscope: +45% UP for top mount, -45% DOWN for bottom mount.
    autoVBias = (projector.topBottomDirection === 'bottom') ? -45 : 45;
  }
  const lensShiftH = (parseFloat(projector.lens_shift_h) || 0) / 100;
  const lensShiftV = ((parseFloat(projector.lens_shift_v) || 0) + autoVBias) / 100;
  const shiftX = lensShiftH * imgW;
  const shiftY = lensShiftV * imgH;

  // Centre and corners of the throw-plane image, including lens shift.
  const centreOnPlane = origin
    .add(direction.scale(throwDist))
    .add(right.scale(shiftX))
    .add(trueUp.scale(shiftY));
  const cornerOnPlane = (sx, sy) =>
    centreOnPlane.add(right.scale(sx * halfW)).add(trueUp.scale(sy * halfH));

  const targets = {
    c:  centreOnPlane,                  // centre (with lens shift)
    tl: cornerOnPlane(-1, +1),          // top-left
    tr: cornerOnPlane(+1, +1),          // top-right
    bl: cornerOnPlane(-1, -1),          // bottom-left
    br: cornerOnPlane(+1, -1),          // bottom-right
  };

  // Cast a ray towards each target. Use a tiny offset along direction so we
  // never start a ray inside the projector body / lens.
  const rayOrigin = origin.add(direction.scale(0.01));
  const hits = {};      // raw mesh hits per ray
  const hitMeshes = {}; // mesh ref per ray (so we can identify primary)
  const hitMeshNames = [];

  for (const k of ['c', 'tl', 'tr', 'bl', 'br']) {
    const dir = targets[k].subtract(origin).normalize();
    const hit = castSurfaceRay(scene, rayOrigin, dir, ownNameSuffix);
    hits[k] = hit ? hit.point : null;
    hitMeshes[k] = hit ? hit.mesh : null;
    if (hit && hit.mesh) hitMeshNames.push(hit.mesh.name || '');
  }

  // Identify a "primary surface" — prefer the mesh hit by the centre ray,
  // since that's what the user is aiming at. Fall back to the most-frequent
  // mesh hit by the corner rays if the centre missed.
  let primaryMesh = hitMeshes.c;
  let primaryName = primaryMesh ? primaryMesh.name : null;
  if (!primaryMesh) {
    const counts = {};
    hitMeshNames.forEach((n) => { counts[n] = (counts[n] || 0) + 1; });
    let best = 0;
    Object.entries(counts).forEach(([n, c]) => {
      if (c > best) {
        best = c;
        primaryName = n;
        primaryMesh = scene.getMeshByName(n);
      }
    });
  }

  // No primary surface → projector points at empty space.
  if (!primaryMesh || !hits.c) {
    return {
      status: 'no-surface',
      topEdge: 0, bottomEdge: 0, leftEdge: 0, rightEdge: 0, diagonal: 0,
      throwDistanceCenter: hits.c ? BABYLON.Vector3.Distance(origin, hits.c) : 0,
      primarySurfaceName: null, surfaceMatchPct: 0,
      cornerHits: { ...hits },
    };
  }

  // Build the primary surface's PLANE from the centre hit point + a normal.
  // Corner rays that missed the actual mesh (because the beam overshoots a
  // small surface) are projected onto this plane analytically.
  //
  // Strategy: prefer the mesh's actual world-space normal at the centre hit
  // (handles tilted/keystoned surfaces correctly), but only when it's a
  // sensible unit vector. Fall back to -direction (a plane perpendicular to
  // the projector's optical axis at the centre-hit distance) — that's the
  // canonical "image footprint at this throw" model and matches the
  // existing Beam/Throw computation, so the two readouts stay coherent.
  let planeNormal = null;
  try {
    const centreDir = targets.c.subtract(origin).normalize();
    const centreRay = new BABYLON.Ray(rayOrigin, centreDir, MAX_RAY_DISTANCE);
    const centreInfo = scene.pickWithRay(centreRay, (m) => m === primaryMesh, false);
    if (centreInfo && centreInfo.hit && typeof centreInfo.getNormal === 'function') {
      const wn = centreInfo.getNormal(true, true);
      // Only trust the normal when it's an actual unit vector pointing back
      // toward the projector (face is visible from this side). A normal
      // pointing the same way as `direction` would be a back-face hit.
      if (wn && isFinite(wn.x) && isFinite(wn.y) && isFinite(wn.z)) {
        const len = Math.sqrt(wn.x * wn.x + wn.y * wn.y + wn.z * wn.z);
        if (len > 0.5) {
          let n = new BABYLON.Vector3(wn.x / len, wn.y / len, wn.z / len);
          // Flip toward projector if facing away.
          if (BABYLON.Vector3.Dot(n, direction) > 0) {
            n = n.scale(-1);
          }
          planeNormal = n;
        }
      }
    }
  } catch (_) { /* fall through to -direction */ }
  if (!planeNormal) {
    planeNormal = new BABYLON.Vector3(-direction.x, -direction.y, -direction.z);
  }

  const planePoint = hits.c;
  // Ray–plane intersection: t = ((planePoint - origin) · normal) / (rayDir · normal)
  const rayPlaneHit = (rayDir) => {
    const denom = BABYLON.Vector3.Dot(rayDir, planeNormal);
    if (Math.abs(denom) < 1e-6) return null; // parallel
    const t = BABYLON.Vector3.Dot(planePoint.subtract(rayOrigin), planeNormal) / denom;
    if (t <= 0) return null; // behind origin
    return rayOrigin.add(rayDir.scale(t));
  };

  // For corners that missed the actual mesh, fill in the plane intersection.
  // Track which corners "spilled off the mesh" for the status badge.
  let spilled = 0;
  for (const k of ['tl', 'tr', 'bl', 'br']) {
    if (hits[k]) continue;
    const dir = targets[k].subtract(origin).normalize();
    const onPlane = rayPlaneHit(dir);
    if (onPlane) {
      hits[k] = onPlane;
      spilled += 1;
    }
  }

  const cornerHitCount = ['tl', 'tr', 'bl', 'br'].reduce((n, k) => n + (hits[k] ? 1 : 0), 0);
  if (cornerHitCount === 0) {
    return {
      status: 'no-surface',
      topEdge: 0, bottomEdge: 0, leftEdge: 0, rightEdge: 0, diagonal: 0,
      throwDistanceCenter: BABYLON.Vector3.Distance(origin, hits.c),
      primarySurfaceName: primaryName, surfaceMatchPct: 0,
      cornerHits: { ...hits },
    };
  }

  // surfaceMatchPct counts only corners that hit the actual primary mesh
  // (not the plane fallback). 1.0 = beam fully contained on the surface.
  const cornersOnMesh = ['tl', 'tr', 'bl', 'br'].filter(
    (k) => hitMeshes[k] && hitMeshes[k] === primaryMesh
  ).length;
  const surfaceMatchPct = cornersOnMesh / 4;
  const status = cornersOnMesh === 4 ? 'ok' : 'mixed';

  // Edge lengths — only meaningful when both endpoints exist.
  const dist = (a, b) => (a && b) ? BABYLON.Vector3.Distance(a, b) : 0;

  // UST OVERRIDE: report the projector calculator's formula values
  // (image_w = throw_distance / throw_ratio, image_h = image_w / aspect)
  // instead of the geometric ray-cast measurements. UST's steep off-axis
  // projection makes ray-cast measurements diverge from the calculator
  // value when the wall isn't exactly perpendicular to the apex-direction
  // ray, which is confusing for users. Per user request, UST inspector
  // ALWAYS shows the spec / calculator value so it matches "Projected Image
  // Size" exactly.
  if (projector.lensType === 'ust') {
    const ustDiag = Math.sqrt(imgW * imgW + imgH * imgH);
    return {
      status,
      topEdge: imgW,
      bottomEdge: imgW,
      leftEdge: imgH,
      rightEdge: imgH,
      diagonal: ustDiag,
      throwDistanceCenter: hits.c ? BABYLON.Vector3.Distance(origin, hits.c) : 0,
      primarySurfaceName: primaryName,
      surfaceMatchPct,
      cornerHits: hits,
    };
  }

  return {
    status,
    topEdge: dist(hits.tl, hits.tr),
    bottomEdge: dist(hits.bl, hits.br),
    leftEdge: dist(hits.tl, hits.bl),
    rightEdge: dist(hits.tr, hits.br),
    diagonal: dist(hits.tl, hits.br),
    throwDistanceCenter: hits.c ? BABYLON.Vector3.Distance(origin, hits.c) : 0,
    primarySurfaceName: primaryName,
    surfaceMatchPct,
    cornerHits: hits,
  };
}
